/**
 * Phase 7 · `.flip-card` click/keyboard toggle handler.
 * Ports `initFlipCards()` from `01_BUILD/qa_studio.html` L22751.
 *
 * Stateless visual toggle — flipped state lives on DOM via `.flipped` class.
 * (No persistence per spec — flip is ephemeral demonstrative interaction.)
 */
export function initFlipCards(root: HTMLElement): () => void {
  const cards = Array.from(root.querySelectorAll<HTMLElement>('.flip-card'));
  const cleanups: Array<() => void> = [];

  for (const card of cards) {
    if (card.dataset.flipInit === '1') continue;
    card.dataset.flipInit = '1';
    if (!card.hasAttribute('tabindex')) card.tabIndex = 0;
    if (!card.hasAttribute('role')) card.setAttribute('role', 'button');
    if (!card.hasAttribute('aria-pressed')) card.setAttribute('aria-pressed', 'false');

    const toggle = () => {
      const flipped = card.classList.toggle('flipped');
      card.setAttribute('aria-pressed', flipped ? 'true' : 'false');
    };

    const onClick = (e: MouseEvent) => {
      // Avoid re-triggering when user clicks an interactive child (link, button, input).
      const target = e.target as HTMLElement;
      if (target.closest('a, button, input, textarea, select, [data-no-flip]')) return;
      toggle();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    };

    card.addEventListener('click', onClick);
    card.addEventListener('keydown', onKeyDown);
    cleanups.push(() => {
      card.removeEventListener('click', onClick);
      card.removeEventListener('keydown', onKeyDown);
      delete card.dataset.flipInit;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
